// §14 — the settings service.
//
// Values live in a vanilla Zustand store (§4.2: engine code holds store handles, React
// subscribes). Persistence is a swappable backend: in-memory here and in tests, OPFS for
// guests and the server for users (M12.8).
import { createStore, type StoreApi } from 'zustand/vanilla';
import type { Bus } from '../bus';
import { serviceToken, type Kernel } from '../registry';
import {
  SETTING_IDS,
  defaultSettings,
  type SettingId,
  type SettingValue,
} from './catalog';

export * from './catalog';

export type SettingsValues = { [K in SettingId]: SettingValue<K> };

export interface SettingsBackend {
  /** Values previously persisted. Unknown keys are ignored; missing keys fall back to defaults. */
  load(): Promise<Partial<Record<string, unknown>>>;
  save(values: SettingsValues): Promise<void>;
}

export function createMemoryBackend(seed: Partial<Record<string, unknown>> = {}): SettingsBackend {
  let stored: Partial<Record<string, unknown>> = { ...seed };
  return {
    async load() {
      return { ...stored };
    },
    async save(values) {
      stored = structuredClone(values);
    },
  };
}

export interface SettingsService {
  get<K extends SettingId>(key: K): SettingValue<K>;
  set<K extends SettingId>(key: K, value: SettingValue<K>): void;
  reset(key: SettingId): void;
  resetAll(): void;
  all(): SettingsValues;
  /** Read persisted values over the defaults. Safe to call once at boot. */
  hydrate(): Promise<void>;
  /** Flush to the backend. `set` schedules this; call it directly to await the write. */
  flush(): Promise<void>;
  /** The vanilla store handle, for React subscriptions in the shell. */
  readonly store: StoreApi<SettingsValues>;
}

export interface SettingsOptions {
  backend?: SettingsBackend;
  /** Milliseconds to coalesce writes. 0 writes on every `set`. */
  saveDebounceMs?: number;
}

export function createSettingsService(bus: Bus, options: SettingsOptions = {}): SettingsService {
  const backend = options.backend ?? createMemoryBackend();
  const debounceMs = options.saveDebounceMs ?? 0;
  const store = createStore<SettingsValues>(() => defaultSettings());

  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight: Promise<void> = Promise.resolve();

  const flush = async (): Promise<void> => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    inFlight = inFlight.then(() => backend.save(store.getState()));
    return inFlight;
  };

  const schedule = (): void => {
    if (debounceMs === 0) {
      void flush();
      return;
    }
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      void flush();
    }, debounceMs);
  };

  const apply = (key: SettingId, value: unknown): void => {
    const before = store.getState()[key];
    if (Object.is(before, value)) return;
    store.setState({ [key]: value } as Partial<SettingsValues>);
    bus.emit('settings:changed', { key, value });
    if (key === 'skin') bus.emit('settings:skinChanged', { skin: value as SettingValue<'skin'> });
    schedule();
  };

  return {
    store,

    get(key) {
      return store.getState()[key];
    },

    set(key, value) {
      apply(key, value);
    },

    reset(key) {
      apply(key, defaultSettings()[key]);
    },

    resetAll() {
      for (const key of SETTING_IDS) this.reset(key);
    },

    all() {
      return { ...store.getState() };
    },

    async hydrate() {
      const persisted = await backend.load();
      const next = defaultSettings();
      for (const key of SETTING_IDS) {
        if (Object.hasOwn(persisted, key)) {
          (next as Record<string, unknown>)[key] = persisted[key];
        }
      }
      store.setState(next, true);
    },

    flush,
  };
}

export const SETTINGS_SERVICE = serviceToken<SettingsService>('settings');

export function provideSettings(kernel: Kernel, options: SettingsOptions = {}): void {
  kernel.provide(SETTINGS_SERVICE, (k) => createSettingsService(k.bus, options));
}
